// @flow
import {observable} from "mobx";

export class Position {
	// The position set via the url, as in `?page=1`, starting at 1.
	@observable page: number = 0;

	// The position of the scroller, starting at 0.
	@observable scroll: number = 0;
	// The position the scroller is currently animating to, relevant for chaining
	// scrolls and for the scroll arrows.
	@observable target: number = 0;
	// The maximum scroll position.
	@observable max: number = 0;
}

export type Edges = Array<number>;
export type Direction = "start" | "middle" | "end";

// This just inserts `elem` into `array` in sorted order.
// We need this because flexbox may reorder children. And also we want to
// ignore duplicates.
export function insert(array: Array<number>, elem: number) {
	for (let i = array.length - 1; i >= 0; i--) {
		const _elem = array[i];
		if (_elem === elem) { return; } // ignore duplicates while we are at it
		if (_elem > elem) { continue; }
		array.splice(i + 1, 0, elem);
		return;
	}
	array.unshift(elem);
}

export function findEdges(elem: Element, horizontal: boolean) {
	const edges: Edges = [0];

	let child = elem.firstChild;
	do {
		if (!(child instanceof HTMLElement)) {
			continue;
		}
		// ignore children with absolute/fixed positioning, such as the arrows
		const position = getComputedStyle(child).position;
		if (position !== "relative" && position !== "static") { continue; }

		const edge =
			(horizontal ? child.offsetLeft : child.offsetTop) +
			(horizontal ? child.offsetWidth : child.offsetHeight);
		insert(edges, edge);
	} while (child && (child = child.nextSibling));

	const edge = horizontal ? elem.scrollWidth : elem.scrollHeight;
	insert(edges, edge);
	return edges;
}

export function findEdgeIndex(
	edges: Edges, edge: number, from: Direction = "start") {
	let position = 0, distance = from === "end" ? Infinity : -Infinity;
	const len = edges.length;
	for (let i = 0; i < len; i++) {
		const index = from === "end" ? len - 1 - i : i;
		const _edge = edges[index];
		const _distance = _edge - edge;
		const isNearer = Math.abs(_distance) < Math.abs(distance);
		const isRightDirection =
			from === "end" ? (_distance >= 0) :
			from === "start" ? (_distance <= 0) :
			true;
		if (isNearer && isRightDirection) {
			position = index;
			distance = _distance;
		} else {
			break;
		}
	}
	return {position, distance};
}

export function findEdgeIndexBoth(edges: Edges, edge: number, prefer: Direction = "start") {
	const start = findEdgeIndex(edges, edge, "start");
	const end = findEdgeIndex(edges, edge, "end");
	const sD = Math.abs(start.distance);
	const eD = Math.abs(end.distance);
	return sD < eD || (sD === eD && prefer === "start")
		? start
		: end;
}

export function findNearestEdge(
	edges: Edges, startEdge: number, endEdge: number) {
	const start = findEdgeIndexBoth(edges, startEdge, "start");
	const end = findEdgeIndexBoth(edges, endEdge, "end");

	const sD = Math.abs(start.distance);
	const eD = Math.abs(end.distance);
	const startIsNearer = sD < eD || (sD === eD && start.distance >= 0);

	const position = startIsNearer ? start.position : end.position - 1;
	const distance = startIsNearer ? start.distance : end.distance;

	let direction = startIsNearer ? "start" : "end";
	if (edges[position] <= startEdge && endEdge <= edges[position + 1]) {
		direction = "middle";
	}
	return {position, direction, distance};
}



// WEBPACK FOOTER //
// ./lib/scroller/utils.js